import PropTypes from 'prop-types';
import React, { useState, useMemo, useEffect, useRef } from "react";
import client from "../../../shared/api/client";
import Popup from "reactjs-popup";
import Flatpickr from "react-flatpickr";
import { toastError } from "../../../shared/toastHelper";
import "../styles/makeRecurringModalStyles.css";

const DAYS = [
  { num: 0, label: "S" },
  { num: 1, label: "M" },
  { num: 2, label: "T" },
  { num: 3, label: "W" },
  { num: 4, label: "T" },
  { num: 5, label: "F" },
  { num: 6, label: "S" },
];

export const formatDateForAPI = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const MakeRecurringModal = ({ open, confirm, team, onClose, onCreated }) => {
  const sourceDate = new Date(confirm.start_time);
  // O-7220: once series and collections are separate (setup wizard on), a show may
  // sit in only one recurring series, and a collection is no reason to warn.
  const blockedReason = confirm.one_recurring_series ? confirm.make_recurring_blocked_reason : null;
  const alreadyInSeries = !confirm.one_recurring_series && (confirm.event_series || []).length > 0;
  const initialStep = blockedReason ? "blocked" : alreadyInSeries ? "warning" : "form";

  const [step, setStep] = useState(initialStep);
  const [startDate, setStartDate] = useState(sourceDate);
  const [endDate, setEndDate] = useState(null);
  const [daysOfWeek, setDaysOfWeek] = useState(new Set([sourceDate.getDay()]));
  const [excludedDates, setExcludedDates] = useState(new Set());
  const [preview, setPreview] = useState({ occurrences: [], count: 0, errors: {} });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const previewSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setStep(initialStep);
    setStartDate(sourceDate);
    setEndDate(null);
    setDaysOfWeek(new Set([sourceDate.getDay()]));
    setExcludedDates(new Set());
    setPreview({ occurrences: [], count: 0, errors: {} });
    setSubmitting(false);
  }, [open, confirm.id]);

  const previewParams = useMemo(() => ({
    name: confirm.title,
    source_calendar_event_id: confirm.id,
    start_date: startDate ? formatDateForAPI(startDate) : null,
    end_date: endDate ? formatDateForAPI(endDate) : null,
    days_of_week: [...daysOfWeek],
    excluded_dates: [...excludedDates],
  }), [confirm.id, confirm.title, startDate, endDate, daysOfWeek, excludedDates]);

  useEffect(() => {
    if (!open || step !== "form") return;
    if (!previewParams.start_date || !previewParams.end_date || previewParams.days_of_week.length === 0) {
      setPreview({ occurrences: [], count: 0, errors: {} });
      return;
    }
    const seq = ++previewSeq.current;
    setPreviewLoading(true);
    const handle = setTimeout(() => {
      client.post(`/teams/${team.id}/event_series/validate`, { event_series: previewParams })
        .then(({ data }) => {
          if (previewSeq.current !== seq) return;
          setPreview(data);
        })
        .catch(() => {
          if (previewSeq.current !== seq) return;
          setPreview({ occurrences: [], count: 0, errors: { base: ["Could not load preview."] } });
        })
        .finally(() => {
          if (previewSeq.current === seq) setPreviewLoading(false);
        });
    }, 200);
    return () => clearTimeout(handle);
  }, [open, step, team.id, previewParams]);

  const toggleDay = (n) => {
    const next = new Set(daysOfWeek);
    next.has(n) ? next.delete(n) : next.add(n);
    setDaysOfWeek(next);
  };

  const toggleExcluded = (iso) => {
    const next = new Set(excludedDates);
    next.has(iso) ? next.delete(iso) : next.add(iso);
    setExcludedDates(next);
  };

  const hasErrors = Object.keys(preview.errors || {}).length > 0;
  const submitDisabled = previewLoading || hasErrors || preview.count < 2 || submitting;
  const errorList = Object.values(preview.errors || {}).flat();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await client.post(`/teams/${team.id}/event_series`, {
        event_series: {
          name: confirm.title,
          source_calendar_event_id: confirm.id,
          start_date: formatDateForAPI(startDate),
          end_date: formatDateForAPI(endDate),
          days_of_week: [...daysOfWeek],
          excluded_dates: [...excludedDates],
        },
      });
      onCreated(data);
    } catch (e) {
      const errs = e?.response?.data?.errors;
      toastError(
        errs ? Object.values(errs).flat().join(", ") : "Could not create this series. Please try again."
      );
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(initialStep);
    onClose();
  };

  return (
    <Popup
      open={open}
      closeOnDocumentClick={!submitting}
      className="make-recurring-popup"
      onClose={handleClose}
    >
      <div className="popup-modal-container">
        <div className="row m-0 make-recurring-header">
          <div className="col d-flex py-3 px-4">
            <h3 className="mb-0 align-self-center make-recurring-title">
              <strong>Make this a recurring event</strong>
            </h3>
          </div>
          <div className="col-xs-auto d-flex align-items-center pr-4">
            <a
              href="#"
              className="text-dark make-recurring-close"
              onClick={(e) => {
                e.preventDefault();
                if (!submitting) handleClose();
              }}
            >
              <i className="fal fa-times"></i>
            </a>
          </div>
        </div>

        <div className="row m-0">
          <div className="col-12 make-recurring-body">
            {step === "blocked" ? (
              <>
                <p>{blockedReason}</p>
                <div className="d-flex justify-content-end make-recurring-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary make-recurring-btn"
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : step === "warning" ? (
              <>
                <p>
                  This event is already part of a recurring series:{" "}
                  <strong>{confirm.event_series[0].name}</strong>.
                  Continuing will create a second series from this event.
                </p>
                <div className="d-flex justify-content-end make-recurring-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary make-recurring-btn"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary make-recurring-btn"
                    onClick={() => setStep("form")}
                  >
                    <strong>Continue anyway</strong>
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-muted make-recurring-help">
                  Set the schedule for your series. Each event will be a copy of this one — tickets, lineup, show details, and all.
                </p>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="form-label make-recurring-label">
                      <strong>Start date</strong>
                    </label>
                    <div className="flatpickr-date-input px-2 d-flex align-items-center">
                      <div className="pr-2 make-recurring-calendar-icon">
                        <i className="far fa-calendar"></i>
                      </div>
                      <Flatpickr
                        options={{ dateFormat: "M j, Y" }}
                        placeholder="Select date"
                        value={startDate || ""}
                        onClose={([date]) => setStartDate(date)}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <label className="form-label make-recurring-label">
                      <strong>End date</strong>
                    </label>
                    <div className="flatpickr-date-input px-2 d-flex align-items-center">
                      <div className="pr-2 make-recurring-calendar-icon">
                        <i className="far fa-calendar"></i>
                      </div>
                      <Flatpickr
                        options={{ dateFormat: "M j, Y", minDate: startDate || undefined }}
                        placeholder="Select date"
                        value={endDate || ""}
                        onClose={([date]) => setEndDate(date)}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label make-recurring-label">
                    <strong>Repeat on</strong>
                  </label>
                  <div className="d-flex make-recurring-days">
                    {DAYS.map(d => {
                      const selected = daysOfWeek.has(d.num);
                      return (
                        <button
                          key={d.num}
                          type="button"
                          className={`btn p-0 make-recurring-day ${selected ? "make-recurring-day-selected" : ""}`}
                          onClick={() => toggleDay(d.num)}
                        >
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {errorList.length > 0 && (
                  <div className="alert alert-warning make-recurring-errors py-2">
                    {errorList.map((msg, i) => <div key={i}>{msg}</div>)}
                  </div>
                )}

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-baseline mb-2">
                    <label className="form-label mb-0 make-recurring-label">
                      <strong>Schedule</strong>
                    </label>
                    <span className="make-recurring-count">
                      <strong>{preview.count}</strong> events
                    </span>
                  </div>
                  <ul className="list-unstyled m-0 make-recurring-schedule">
                    {preview.occurrences.map(occ => {
                      const included = occ.is_source || !excludedDates.has(occ.iso);
                      return (
                        <li
                          key={occ.iso}
                          className="d-flex align-items-center make-recurring-schedule-row"
                        >
                          <input
                            type="checkbox"
                            className="mr-3 make-recurring-checkbox"
                            checked={included}
                            disabled={occ.is_source}
                            onChange={() => toggleExcluded(occ.iso)}
                          />
                          <span className={`make-recurring-schedule-date ${included ? "" : "make-recurring-schedule-date-excluded"}`}>
                            {occ.label}
                            {occ.is_source && <span className="text-muted ml-2">(this event)</span>}
                          </span>
                        </li>
                      );
                    })}
                    {!previewLoading && preview.occurrences.length === 0 && (
                      <li className="text-muted make-recurring-empty">
                        No dates match your selection.
                      </li>
                    )}
                  </ul>
                </div>

                <div className="d-flex justify-content-end make-recurring-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary make-recurring-btn"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary make-recurring-btn"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Creating...
                      </>
                    ) : (
                      <strong>Create {preview.count} events</strong>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Popup>
  );
};

MakeRecurringModal.propTypes = {
  open: PropTypes.bool.isRequired,
  confirm: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default MakeRecurringModal;
